// Rattlebone Staff Hub — build 2026-07-24
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Toaster } from '@/components/ui/sonner';
import DevAuthProvider, { useDevAuth } from './components/DevAuthProvider';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Onboarding from './pages/Onboarding';
import Documents from './pages/Documents';
import ModuleLibrary from './pages/ModuleLibrary';
import LessonViewer from './pages/LessonViewer';
import Quiz from './pages/Quiz';
import Journal from './pages/Journal';
import Certificates from './pages/Certificates';
import ManagerDashboard from './pages/ManagerDashboard';
import AdminPanel from './pages/AdminPanel';
import SOPLibrary from './pages/SOPLibrary';
import TrainingAssistant from './pages/TrainingAssistant';
import Profile from './pages/Profile';
import MyJourney from './pages/MyJourney';
import Systems from './pages/Systems';
import Compliance from './pages/Compliance';
import TrainingRequests from './pages/TrainingRequests';
import Notifications from './pages/Notifications';
import Reviews from './pages/Reviews';
import CustomerReviews from './pages/CustomerReviews';
import Handbook from './pages/Handbook';
import Policies from './pages/Policies';
import Uniform from './pages/Uniform';
import MyKeys from './pages/MyKeys';
import MyRewards from './pages/MyRewards';
import Benefits from './pages/Benefits';
import Suggestions from './pages/Suggestions';
import EmployeeOfMonth from './pages/EmployeeOfMonth';
import QuizHistory from './pages/QuizHistory';
import PubInfo from './pages/PubInfo';
import HealthSafety from './pages/HealthSafety';
import CoshhRegister from './pages/CoshhRegister';
import Maintenance from './pages/Maintenance';
import Tools from './pages/Tools';
import GpCalculator from './pages/GpCalculator';
import ShiftPrep from './pages/ShiftPrep';
import EventSetup from './pages/EventSetup';
import Checklists from './pages/Checklists';
import MyTools from './pages/MyTools';
import FloorPlans from './pages/FloorPlans';
import SharedDocuments from './pages/SharedDocuments';
import FireLog from './pages/FireLog';
import ManagerTasks from './pages/ManagerTasks';
import DataBackup from './pages/DataBackup';
import SystemDocumentation from './pages/SystemDocumentation';
import HandoverDocuments from './pages/HandoverDocuments';
import ActionQueue from './pages/ActionQueue';
import DevelopmentBoard from './pages/DevelopmentBoard';
import BridgeHealth from './pages/BridgeHealth';
import ComplianceExpiry from './pages/ComplianceExpiry';
import EmailSafety from './pages/EmailSafety';
import PersonalTasksPage from './pages/PersonalTasks';
import PermissionMatrix from './pages/PermissionMatrix';
import LeaveRequestsPage from './pages/LeaveRequests';
import DailyOpsBridge from './pages/DailyOpsBridge';
import TrainingAnalytics from './pages/TrainingAnalytics';
import DocumentAccessLog from './pages/DocumentAccessLog';
import TeamReadiness from './pages/TeamReadiness';
import TrainingBuilder from './pages/TrainingBuilder';
import OnboardingJourneysPage from './pages/OnboardingJourneys';
import ComplianceCertificates from './pages/ComplianceCertificates';
import InfoHub from './pages/InfoHub';
import Recognition from './pages/Recognition';
import FeedbackCentre from './pages/FeedbackCentre';
import DocumentLibrary from './pages/DocumentLibrary';
import ReportsCentre from './pages/ReportsCentre';
import EngagementInsights from './pages/EngagementInsights';
import AuditReadiness from './pages/AuditReadiness';
import ManagerOps from './pages/ManagerOps';
import FeatureAudit from './pages/FeatureAudit';
import DataQuality from './pages/DataQuality';
import TrainingRenewals from './pages/TrainingRenewals';
import AvailabilityCalendar from './pages/AvailabilityCalendar';
import PayLeave from './pages/PayLeave';
import MyPayslips from './pages/MyPayslips';
import MyShifts from './pages/MyShifts';
import AppSettings from './pages/AppSettings';
import FeatureToggleProvider from './components/FeatureToggleProvider';
import FeatureRouteGuard from './components/FeatureRouteGuard';
import TrainingHome from './pages/TrainingHome';
import CourseDetail from './pages/CourseDetail';
import CourseModuleLearner from './pages/CourseModuleLearner';
import InteractiveLearner from './pages/InteractiveLearner';
import CourseModuleQuiz from './pages/CourseModuleQuiz';
import InvigilatorAssessment from './pages/InvigilatorAssessment';
import CourseCompletion from './pages/CourseCompletion';
import TrainingAdmin from './pages/TrainingAdmin';
import CoursePreview from './pages/CoursePreview';
import MyPolicies from './pages/MyPolicies';
import ImportReport from './pages/ImportReport';
import RefresherSchedule from './pages/RefresherSchedule';
import CourseBuilder from './pages/CourseBuilder';
import MyAssessmentResult from './pages/MyAssessmentResult';
import TrainingVideoLibrary from './pages/TrainingVideoLibrary';
import TeamRoster from './pages/TeamRoster';

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading, loginWithRedirect } = useAuth();
  const { isDevMode, isValidating } = useDevAuth();

  useEffect(() => {
    if (!isDevMode && !isValidating && !isLoading && !user) {
      loginWithRedirect({ redirectUrl: window.location.href });
    }
  }, [isLoading, user, loginWithRedirect, isDevMode, isValidating]);

  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Validating dev token...</p>
        </div>
      </div>
    );
  }

  if (isDevMode) return <>{children}</>;

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading Staff Hub...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <DevAuthProvider>
        <Toaster />
        <AuthGuard>
          <FeatureToggleProvider>
          <Layout>
            <FeatureRouteGuard>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/modules" element={<ModuleLibrary />} />
              <Route path="/modules/:id" element={<LessonViewer />} />
              <Route path="/modules/:id/quiz" element={<Quiz />} />
              <Route path="/journal" element={<Journal />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route path="/manager" element={<ManagerDashboard />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/sops" element={<SOPLibrary />} />
              <Route path="/ask" element={<TrainingAssistant />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/journey" element={<MyJourney />} />
              <Route path="/systems" element={<Systems />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/training-requests" element={<TrainingRequests />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/customer-reviews" element={<CustomerReviews />} />
              <Route path="/handbook" element={<Handbook />} />
              <Route path="/policies" element={<Policies />} />
              <Route path="/uniform" element={<Uniform />} />
              <Route path="/my-keys" element={<MyKeys />} />
              <Route path="/my-rewards" element={<MyRewards />} />
              <Route path="/benefits" element={<Benefits />} />
              <Route path="/suggestions" element={<Suggestions />} />
              <Route path="/employee-of-month" element={<EmployeeOfMonth />} />
              <Route path="/quiz-history" element={<QuizHistory />} />
              <Route path="/pub-info" element={<PubInfo />} />
              <Route path="/health-safety" element={<HealthSafety />} />
              <Route path="/coshh" element={<CoshhRegister />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/gp-calculator" element={<GpCalculator />} />
              <Route path="/shift-prep" element={<ShiftPrep />} />
              <Route path="/event-setup" element={<EventSetup />} />
              <Route path="/checklists" element={<Checklists />} />
              <Route path="/my-tools" element={<MyTools />} />
              <Route path="/floor-plans" element={<FloorPlans />} />
              <Route path="/shared-documents" element={<SharedDocuments />} />
              <Route path="/fire-log" element={<FireLog />} />
              <Route path="/tasks" element={<ManagerTasks />} />
              <Route path="/data-backup" element={<DataBackup />} />
              <Route path="/system-docs" element={<SystemDocumentation />} />
              <Route path="/handover-docs" element={<HandoverDocuments />} />
              <Route path="/actions" element={<ActionQueue />} />
              <Route path="/dev-board" element={<DevelopmentBoard />} />
              <Route path="/bridge-health" element={<BridgeHealth />} />
              <Route path="/compliance-expiry" element={<ComplianceExpiry />} />
              <Route path="/email-safety" element={<EmailSafety />} />
              <Route path="/my-tasks" element={<PersonalTasksPage />} />
              <Route path="/permission-matrix" element={<PermissionMatrix />} />
              <Route path="/leave" element={<LeaveRequestsPage />} />
              <Route path="/daily-ops-bridge" element={<DailyOpsBridge />} />
              <Route path="/training-analytics" element={<TrainingAnalytics />} />
              <Route path="/document-access-log" element={<DocumentAccessLog />} />
              <Route path="/team-readiness" element={<TeamReadiness />} />
              <Route path="/training-builder" element={<TrainingBuilder />} />
              <Route path="/onboarding-journeys" element={<OnboardingJourneysPage />} />
              <Route path="/compliance-certs" element={<ComplianceCertificates />} />
              <Route path="/info-hub" element={<InfoHub />} />
              <Route path="/recognition" element={<Recognition />} />
              <Route path="/feedback" element={<FeedbackCentre />} />
              <Route path="/document-library" element={<DocumentLibrary />} />
              <Route path="/reports" element={<ReportsCentre />} />
              <Route path="/engagement" element={<EngagementInsights />} />
              <Route path="/audit-readiness" element={<AuditReadiness />} />
              <Route path="/manager-ops" element={<ManagerOps />} />
              <Route path="/feature-audit" element={<FeatureAudit />} />
              <Route path="/data-quality" element={<DataQuality />} />
              <Route path="/training-renewals" element={<TrainingRenewals />} />
              <Route path="/availability" element={<AvailabilityCalendar />} />
              <Route path="/my-shifts" element={<MyShifts />} />
              <Route path="/pay-leave" element={<PayLeave />} />
              <Route path="/payslips" element={<MyPayslips />} />
              <Route path="/app-settings" element={<AppSettings />} />
              <Route path="/train" element={<TrainingHome />} />
              <Route path="/train/:enrollmentId" element={<CourseDetail />} />
              <Route path="/train/:enrollmentId/module/:moduleId" element={<CourseModuleLearner />} />
              <Route path="/train/:enrollmentId/module/:moduleId/v2" element={<InteractiveLearner />} />
              <Route path="/train/:enrollmentId/module/:moduleId/quiz" element={<CourseModuleQuiz />} />
              <Route path="/train/assess/:sessionId" element={<InvigilatorAssessment />} />
              <Route path="/train/:enrollmentId/completion" element={<CourseCompletion />} />
              <Route path="/train/admin" element={<TrainingAdmin />} />
              <Route path="/train/admin/preview/:courseId" element={<CoursePreview />} />
              <Route path="/train/admin/builder/:courseId" element={<CourseBuilder />} />
              <Route path="/train/assessment-result/:sessionId" element={<MyAssessmentResult />} />
              <Route path="/training-videos" element={<TrainingVideoLibrary />} />
              <Route path="/team-roster" element={<TeamRoster />} />
              <Route path="/team-roster" element={<TeamRoster />} />
              <Route path="/team-roster" element={<TeamRoster />} />
              <Route path="/team-roster" element={<TeamRoster />} />
              <Route path="/my-policies" element={<MyPolicies />} />
              <Route path="/import-report" element={<ImportReport />} />
              <Route path="/refresher-schedule" element={<RefresherSchedule />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </FeatureRouteGuard>
          </Layout>
          </FeatureToggleProvider>
        </AuthGuard>
      </DevAuthProvider>
    </BrowserRouter>
  );
}
